import { Card } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Divider from "../../components/Divider/Divider";
import DocumentationPage from "../../components/DocumentationPage/DocumentationPage";

const DocumentationIntroduction = () => {
  return (
    <>
      <DocumentationPage heading='Documentation - Introduction'>
        <Helmet>
          <title>Smash Documentation - Introduction</title>
          <meta name="description" content="Welcome to the official Smash developer documentation. Learn how to integrate Smash's file transfer capabilities into your applications using our RESTful API and SDKs." />
        </Helmet>
        <div className="row">
          <div className="col">
            <p>
              Welcome to the official Smash developer documentation. This guide provides everything you need to integrate Smash's file transfer capabilities into your applications using our RESTful API and SDKs.
            </p>
            <p>
              Whether you're building backend services, frontend applications, or automation scripts, the Smash API and SDKs offer the tools to:
            </p>
            <ul>
              <li>Send and receive large files programmatically</li>
              <li>Monitor file transfer activity in real-time</li>
              <li>Manage users and permissions</li>
              <li>Access detailed analytics and file usage statistics</li>
              <li>Leverage advanced features for security, customization, and workflow automation</li>
            </ul>
            <p>
              This documentation includes comprehensive guides on authentication, endpoint usage, SDK installation, code samples, and best practices to help you get the most out of the Smash platform.
            </p>
          </div>
          <div className="col">
            <Card>
              <Card.Body>
                <h2 className="">Need an account?</h2>
                <p>
                  If you don't have an account yet, you can create one by clicking the button below.
                  It's free and only takes a few minutes!
                </p>
                <a href="https://developer.fromsmash.com/signup" className="btn btn-primary">
                  Create an account
                </a>
              </Card.Body>
            </Card>
          </div>
        </div>

        <Divider />

        <div className="row">
          <div className="col">
            <h2>API</h2>
            <p>
              The Smash REST API provides scalable way to programmatically interact with Smash's file-sharing platform. Designed with flexibility in mind, the API allows developers to automate workflows, integrate file transfers into custom applications, and manage users and activity across their Smash environment.
            </p>
            <p>
              Built following RESTful principles, the API uses standard HTTP methods and returns JSON-formatted responses. It is secured using token-based authentication, ensuring that only authorized clients can access protected resources.
            </p>
            <p>
              For more information on how to use the API, please refer to the <Link to="/docs/APIReference" className="fw-bold">API Reference</Link> section.
            </p>
          </div>
        </div>

        <Divider />

        <div className="row">
          <div className="col">
            <h2>SDKs</h2>
            <p>
              Smash provides a set of SDKs to help developers integrate various services of the Smash platform into their applications. These SDKs are designed to be modular, allowing you to use only the features you need.
            </p>
            <p>
              The main entry point is the <a href="https://github.com/fromsmash/smash-sdk-js" target="_blank"><strong>smash-sdk-js</strong></a> repository, which contains SDKs for interacting with core services.
            </p>
            <p>
              These SDKs provide a low-level, flexible interface to the Smash REST API, ideal for developers who need full control over the request/response cycle or want to build highly customized integrations.
            </p>
            <p>
              In addition to these foundational SDKs, Smash also offers specialized libraries that encapsulate common workflows and add advanced features:
            </p>
            <ul>
              <li>
                <a href="https://github.com/fromsmash/smash-uploader-js" target="_blank"><strong>smash-uploader-js</strong></a> — Simplifies file uploads with built-in support for chunking, resumable uploads, and progress tracking.
              </li>
              <li>
                <a href="https://github.com/fromsmash/smash-downloader-js" target="_blank"><strong>smash-downloader-js</strong></a> — Provides high-level tools to handle secure, optimized file downloads from Smash.
              </li>
            </ul>
            <p>
              These higher-level libraries are ideal for developers looking to implement upload/download functionality quickly, without needing to manage the lower-level API interactions manually.
            </p>
            <p>
              All packages are available on <code>npm</code>, written in TypeScript, and designed to be easy to integrate into any Node.js project.
            </p>
          </div>
        </div>

        <Divider />
        <div className="row">
          <div className="col">
            <h2>Examples</h2>
            <p>
              To help you get started quickly, we've published a set of practical integration examples using our official SDKs and libraries. These examples demonstrate real-world usage patterns, including file uploads, downloads and more.
            </p>
            <p>
              You can explore the examples on <a href="https://github.com/fromsmash/example-js" target="_blank"><strong>GitHub</strong></a>.
            </p>
            <p>
              We encourage you to explore these examples and adapt them to your own use cases. They are a great starting point for building your own applications on top of the Smash platform.
            </p>
          </div>
        </div>
      </DocumentationPage>
    </>
  );
};

export default DocumentationIntroduction;
